import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { useAddressBookOrganizations } from '../../../hooks/useAddressBook';
import { getContactPointValues, getPrimaryContactPoint } from '../../address-book/contactPoints';
import { XIcon } from '../../../components/ui/Icons';

interface ClientAutocompleteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (clientId: number) => void;
}

const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({ isOpen, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { organizations, isLoading } = useAddressBookOrganizations(isOpen);
  const clients = organizations;

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    const clientsList = clients || [];
    if (!clientsList || clientsList.length === 0) return [];
    
    if (!searchTerm.trim()) {
      return clientsList.slice(0, 50); // Limit to 50 for performance
    }

    const term = searchTerm.toLowerCase();
    return clientsList
      .filter((client) => {
        const name = client.clientName?.toLowerCase() || '';
        const name2 = client.clientName2?.toLowerCase() || '';
        const code = client.clientCode?.toLowerCase() || '';
        const emails = getContactPointValues(client.contactPoints, 'EMAIL').join(' ').toLowerCase();
        const phones = getContactPointValues(client.contactPoints, 'PHONE').join(' ').toLowerCase();
        const contactPerson = client.contactPerson?.toLowerCase() || '';
        
        return (
          name.includes(term) ||
          name2.includes(term) ||
          code.includes(term) ||
          emails.includes(term) ||
          phones.includes(term) ||
          contactPerson.includes(term)
        );
      })
      .slice(0, 50); // Limit results
  }, [clients, searchTerm]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredClients]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => 
        prev < filteredClients.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredClients[selectedIndex]) {
        handleSelect(filteredClients[selectedIndex].clientId);
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && filteredClients.length > 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, filteredClients]);

  const handleSelect = (clientId: number) => {
    onSelect(clientId);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Link to organization</ModalTitle>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          aria-label="Close"
        >
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by name, code, email, phone, or contact person..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
            <svg
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Results List */}
          <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto" ref={listRef}>
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Loading clients...</div>
            ) : filteredClients.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm ? 'No clients found matching your search.' : 'No clients available.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredClients.map((client, index) => {
                  const isSelected = index === selectedIndex;

                  return (
                    <div
                      key={client.clientId}
                      onClick={() => handleSelect(client.clientId)}
                      className={`p-4 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-indigo-50 border-l-4 border-indigo-500'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-semibold text-sm">
                                {client.clientName?.[0]?.toUpperCase() || 'C'}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {client.clientName || `Organization #${client.clientId}`}
                              </p>
                              {client.clientCode && (
                                <p className="text-xs text-gray-500">Code: {client.clientCode}</p>
                              )}
                            </div>
                          </div>
                          <div className="ml-13 space-y-1">
                            {client.contactPerson && (
                              <p className="text-xs text-gray-600">
                                Contact: {client.contactPerson}
                              </p>
                            )}
                            {(() => {
                              const primaryEmail = getPrimaryContactPoint(client.contactPoints, 'EMAIL');
                              const primaryPhone = getPrimaryContactPoint(client.contactPoints, 'PHONE');
                              return (
                                <>
                                  {primaryEmail && (
                                    <p className="text-xs text-gray-600">
                                      Email: {primaryEmail.value}
                                    </p>
                                  )}
                                  {primaryPhone && (
                                    <p className="text-xs text-gray-600">
                                      Phone: {primaryPhone.value}
                                    </p>
                                  )}
                                </>
                              );
                            })()}
                            {client.clientCity && (
                              <p className="text-xs text-gray-600">
                                {client.clientCity}
                                {client.clientState && `, ${client.clientState}`}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {filteredClients.length > 0 && (
            <div className="text-xs text-gray-500 text-center">
              Use arrow keys to navigate, Enter to select, Esc to close
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientAutocomplete;
